import React, { useState } from "react";
import { PageHeader } from "@/components/shared/SectionHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, MapPin, Phone } from "lucide-react";

const FAQ = [
  { q: "How do I earn the Future Owner Certificate?", a: "Complete the full Future Owner Program and pass the final assessment. The certificate is issued digitally and recognized by participating breeders." },
  { q: "Are courses live or on-demand?", a: "Most courses are on-demand with monthly live Q&As. Workshops and the annual conference are live." },
  { q: "Do you sell wolfdogs?", a: "No. We are an educational institution, not a breeder or marketplace. Our directory connects you to vetted, independent professionals." },
  { q: "Can I mentor as a certified owner?", a: "Yes. Ownership 301 graduates are eligible for our mentorship program." },
  { q: "Do you support international members?", a: "Yes. Our curriculum is used by owners, breeders and sanctuaries around the world." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        eyebrow="Contact"
        title="We'd love to hear from you."
        description="Members, breeders, sanctuaries, veterinarians, media, partners — this is where the village begins."
        image="https://images.unsplash.com/photo-1508905697787-f8b9d67ffb03?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
      />
      <section className="container-x py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 rounded-lg border border-border bg-card p-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><input required value={form.name} onChange={setF("name")} data-testid="contact-name" className="input" /></Field>
            <Field label="Email"><input required type="email" value={form.email} onChange={setF("email")} data-testid="contact-email" className="input" /></Field>
          </div>
          <Field label="Subject" className="mt-4"><input required value={form.subject} onChange={setF("subject")} data-testid="contact-subject" className="input" /></Field>
          <Field label="Message" className="mt-4"><textarea required rows={6} value={form.message} onChange={setF("message")} data-testid="contact-message" className="input resize-none" /></Field>
          <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary mt-6">{busy ? "Sending…" : "Send message"}</button>
        </form>

        <aside className="lg:col-span-5 space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="eyebrow">Institute HQ</div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" /> Missoula, Montana</div>
              <div className="inline-flex items-center gap-2"><Mail className="h-4 w-4" /> hello@wolfdogvillage.org</div>
              <div className="inline-flex items-center gap-2"><Phone className="h-4 w-4" /> +1 (406) 555-0134</div>
            </div>
          </div>
          <div className="aspect-[4/3] rounded-lg overflow-hidden border border-border">
            <iframe
              title="map"
              className="h-full w-full"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-114.2%2C46.7%2C-113.7%2C47.0&layer=mapnik&marker=46.87%2C-113.99"
            />
          </div>
        </aside>
      </section>

      <section className="container-x pb-24">
        <div className="eyebrow">FAQ</div>
        <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight">Answers before you ask.</h2>
        <Accordion type="single" collapsible className="mt-8">
          {FAQ.map((f, i) => (
            <AccordionItem value={`f-${i}`} key={i} data-testid={`faq-${i}`}>
              <AccordionTrigger><span className="font-display text-lg text-left">{f.q}</span></AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <style>{`.input{width:100%;padding:.75rem 1rem;font-size:.875rem;border-radius:.375rem;border:1px solid hsl(var(--border));background:hsl(var(--background));color:hsl(var(--foreground));} .input:focus{outline:none;box-shadow:0 0 0 2px hsl(var(--primary));}`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={"block " + className}>
      <span className="eyebrow">{label}</span>
      <span className="block mt-2">{children}</span>
    </label>
  );
}
